import Component from '@glimmer/component'
import { service } from '@ember/service'
import { colors } from '@colors'
import convertDateFormat from 'teacher-ui-client/utils/giraffe/convert-date-format'
import { formatNumber, formatFixedDecimal } from 'teacher-ui-client/utils/giraffe/formatter'
import { formatDuration } from 'teacher-ui-client/utils/format-duration'
import { FigureBoxes } from 'teacher-ui-client/components/giraffe/figure-boxes'
import { ChartInfoTooltip } from 'teacher-ui-client/components/giraffe/chart-info-tooltip'
import { SeriesColumn, type SeriesColumnOptions } from 'teacher-ui-client/components/giraffe/primitives/series-column'
import type { IntlService } from 'ember-intl'
import { t } from 'ember-intl'
import type { Point, AlignValue } from 'highcharts'
import { ThemeKeys } from 'teacher-ui-client/utils/themes'
import { roundAccurately } from 'teacher-ui-client/utils/round'
import type { UIScope } from 'teacher-ui-client/reporting/ui-scope/route'
import type Store from '@ember-data/store'
import type { TimeOnTaskUsageData } from 'teacher-ui-client/utils/student-events/fetchers/time-on-task-usage'
import type Teacher from 'teacher-ui-client/models/teacher'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import type School from 'teacher-ui-client/models/school'

const secondsInAnHour = 60 * 60

interface Args {
  data: TimeOnTaskUsageData
  uiScope: UIScope
  primaryTheme?: ThemeKeys
  secondaryTheme?: ThemeKeys
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class TimeOnTaskUsage extends Component<Signature> {
  @service intl!: IntlService

  @service store!: Store

  get contributingStudents() {
    return this.args.data.summary.student_count
  }

  get primaryTheme() {
    return this.args.primaryTheme ?? ThemeKeys.default
  }

  get secondaryTheme() {
    return this.args.secondaryTheme ?? ThemeKeys.default
  }

  get totalsStats() {
    const { scopeLabel, detailedScopeLabel } = this
    const { summary } = this.args.data
    const theme = this.primaryTheme
    return {
      data: [
        {
          label: `${scopeLabel} ${this.intl.t('reporting.statBoxes.timeOnTaskUsage.totalHours')}`,
          value: formatNumber(summary.total_seconds / secondsInAnHour, 1),
        },
        {
          label: this.intl.t('reporting.statBoxes.timeOnTaskUsage.avgHoursPerStudent'),
          value: formatFixedDecimal((summary.average_seconds_per_student ?? 0) / secondsInAnHour, 1),
        },
        {
          label: this.intl.t('reporting.statBoxes.timeOnTaskUsage.avgHoursPerStudentPerWeek'),
          value: formatFixedDecimal((summary.average_seconds_per_student_per_week ?? 0) / secondsInAnHour, 1),
        },
      ],
      title: `${detailedScopeLabel} ${this.intl.t('reporting.chartTitles.timeOnTaskTotals')}`,
      theme,
    }
  }

  get sessionsStats() {
    const { intl } = this
    const { summary } = this.args.data
    const theme = this.secondaryTheme
    return {
      data: [
        {
          label: `${this.scopeLabel} ${intl.t('reporting.statBoxes.timeOnTaskUsage.totalSessions')}`,
          value: formatNumber(summary.session_count, 1),
        },
        {
          label: intl.t('reporting.statBoxes.timeOnTaskUsage.avgSessionsPerStudent'),
          value: formatNumber(summary.average_sessions_per_student ?? 0, 1),
        },
        {
          label: intl.t('reporting.statBoxes.timeOnTaskUsage.avgTimeInSession'),
          value: formatDuration(summary.average_seconds_per_session),
        },
      ],
      title: intl.t('reporting.chartTitles.timeOnTaskSessions'),
      theme,
    }
  }

  get columnChart() {
    const { months } = this.args.data

    const colorMap: Partial<Record<ThemeKeys, string>> = {
      blue: colors.oceanyBlue300,
      green: colors.grapeyGreen300,
      'dark-blue': colors.oceanyBlue325,
      'dark-orange': colors.juicyOrange450,
    }

    const color = colorMap[this.primaryTheme] ?? colors.dustyBlack100

    const data: SeriesColumnOptions[] = [
      {
        name: 'hours',
        data: months.map(({ seconds }) => roundAccurately(seconds / secondsInAnHour, 1)),
        color,
        type: 'column',
      },
    ]

    const { intl } = this

    return {
      data,
      titleOptions: {
        align: 'left' as AlignValue,
        text: intl.t('reporting.chartTitles.timeOnTaskUsage'),
      },
      yAxisLabel: intl.t('reporting.yAxisLabels.timeOnTaskUsage'),
      categories: months.map(({ month }) => convertDateFormat(month, 'yyyy-MM', 'MMMM')),

      toolTipFormatter(this: Point) {
        return `${this.y} ${intl.t('hours')}`
      },
    }
  }

  get scopeLabel() {
    const uiScopeName = this.args.uiScope.scope
    return this.intl.t('reporting.timeOnTask.scope.basicLabel', { scope: uiScopeName })
  }

  // Returns a string like "SF School" (school), "John's classes" (teacher) or "Class 3/4" (school-class)
  get detailedScopeLabel() {
    const uiScope = this.args.uiScope

    if (uiScope.scope === 'teacher') {
      const teacherName = this.store.peekRecord<Teacher>('teacher', uiScope.id)?.fullName ?? ''
      return this.intl.t('reporting.timeOnTask.scope.detailedTeacherLabel', { teacherName })
    } else if (uiScope.scope === 'school-class') {
      return this.store.peekRecord<SchoolClass>('school-class', uiScope.id)?.name ?? ''
    } else {
      return this.store.peekRecord<School>('school', uiScope.id)?.name ?? ''
    }
  }

  <template>
    <div class="relative" ...attributes>
      <ChartInfoTooltip @tooltipContent={{t "reporting.tooltips.timeOnTaskUsage"}} />
      <div class="m-3 flex justify-between">
        <div data-test-time-on-task-students class="w-1/2">
          <FigureBoxes
            @figures={{this.totalsStats.data}}
            @title={{this.totalsStats.title}}
            @theme={{this.totalsStats.theme}}
          />
        </div>
        <div data-test-time-on-task-sessions class="w-1/2">
          <FigureBoxes
            @figures={{this.sessionsStats.data}}
            @title={{this.sessionsStats.title}}
            @theme={{this.sessionsStats.theme}}
          />
        </div>
      </div>
      <div data-test-time-on-task-contributing-students class="m-3">
        {{t "reporting.summaryTitles.contributingStudents"}}:
        {{this.contributingStudents}}
      </div>
      <div class="h-60">
        <SeriesColumn
          @data={{this.columnChart.data}}
          @title={{this.columnChart.titleOptions}}
          @categories={{this.columnChart.categories}}
          @yAxisLabel={{this.columnChart.yAxisLabel}}
          @toolTipFormatter={{this.columnChart.toolTipFormatter}}
        />
      </div>
    </div>
  </template>
}

export default TimeOnTaskUsage
